import React from 'react';
import { chartColor } from '../chartTheme';

/** One rounded column on a full-height track. */
export interface ColumnDatum {
  key: string;
  /** Value or period under the column. */
  label: string;
  /** Fill of the track, 0 to 1 (relative to the tallest column). */
  fill: number;
  /** Shown above the column: a percentage, or `n of m` for small populations. */
  valueLabel: string;
  /** Exact values on hover. */
  detail: string;
}

/** Props for {@link ColumnBars}. */
export interface ColumnBarsProps {
  data: ColumnDatum[];
  /** Labels at the low and high ends of a rating scale. */
  ends?: { low: string; high: string };
  onSelect?: (datum: ColumnDatum, index: number) => void;
}

/**
 * Friendly vertical bars for numbers, ratings and dates: rounded columns
 * rising from zero on a soft track, the share above and the value below. No
 * axes or gridlines.
 */
export const ColumnBars: React.FC<ColumnBarsProps> = ({ data, ends, onSelect }) => (
  <div data-chart="columns" className="flex flex-col gap-2">
    <div className="grid items-end gap-2" style={{ gridTemplateColumns: `repeat(${data.length}, minmax(0, 1fr))` }}>
      {data.map((datum, index) => {
        const name = `${datum.label}: ${datum.valueLabel}`;
        const content = (
          <>
            <span className="truncate text-xs tabular-nums text-muted-foreground">{datum.valueLabel}</span>
            <span className="flex h-28 w-full items-end overflow-hidden rounded-xl" style={{ background: chartColor.track }}>
              <span data-mark className="block w-full rounded-xl" style={{ height: `${Math.round(Math.min(1, Math.max(0, datum.fill)) * 100)}%`, background: chartColor.mark }} />
            </span>
            <span className="w-full truncate text-center text-xs text-foreground">{datum.label}</span>
          </>
        );
        const className = 'flex min-w-0 flex-col items-center gap-1.5 rounded-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';
        return onSelect ? (
          <button key={datum.key} type="button" aria-label={name} data-tip={datum.label} data-tip-detail={datum.detail} className={`${className} cursor-pointer`} onClick={() => onSelect(datum, index)}>
            {content}
          </button>
        ) : (
          <div key={datum.key} role="img" aria-label={name} data-tip={datum.label} data-tip-detail={datum.detail} tabIndex={0} className={className}>{content}</div>
        );
      })}
    </div>
    {ends && (
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{ends.low}</span>
        <span>{ends.high}</span>
      </div>
    )}
  </div>
);
